/**
 * WasteDamagePage — factory admin/manager records waste/damage entries.
 * Maker-checker workflow: entries go to pending_approval; HO/Chief approves → stock deducted + journal posted.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/lib/auth";
import { Navigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ColorTab } from "@/components/ui/color-tab";
import { inr, fmtDate } from "@/lib/utils-format";
import { AlertTriangle, Camera, CheckCircle2, XCircle, Trash2, Plus, RefreshCw, TrendingDown, Package, Clock, Search } from "lucide-react";

const CREATOR_ROLES = ["super_admin", "admin", "chief_accountant", "ho_admin", "factory_manager", "factory_admin", "production_manager", "qc_manager"];
const APPROVER_ROLES = ["super_admin", "admin", "chief_accountant", "ho_admin"];
const LOSS_TYPES = [
  { value: "waste",   label: "Waste (raw scrap)",       tone: "bg-orange-100 text-orange-800 border-orange-200", photo: false },
  { value: "damage",  label: "Damage (handling)",       tone: "bg-red-100 text-red-800 border-red-200", photo: true },
  { value: "expired", label: "Expired (write-off)",     tone: "bg-amber-100 text-amber-800 border-amber-200", photo: true },
  { value: "spoiled", label: "Spoiled (QC reject)",     tone: "bg-purple-100 text-purple-800 border-purple-200", photo: false },
  { value: "return",  label: "Customer Return",         tone: "bg-slate-100 text-slate-700 border-slate-200", photo: false },
];
const STATUS_TONE = {
  pending_approval: "bg-amber-100 text-amber-800 border-amber-200",
  approved: "bg-emerald-100 text-emerald-800 border-emerald-200",
  rejected: "bg-red-100 text-red-800 border-red-200",
};

export default function WasteDamagePage() {
  const { user } = useAuth();
  const [tab, setTab] = useState("new");
  const canCreate = user && CREATOR_ROLES.includes(user.role);
  const canApprove = user && APPROVER_ROLES.includes(user.role);
  if (user && !canCreate) return <Navigate to="/app/dashboard" replace />;

  return (
    <div className="p-6 max-w-[1500px] mx-auto space-y-4" data-testid="waste-damage-page">
      <div>
        <div className="text-[11px] font-mono tracking-widest uppercase text-[#C05621]">Manufacturing · Losses</div>
        <h1 className="font-[Outfit] text-3xl font-semibold text-[#2C1810] flex items-center gap-2 mt-1">
          <AlertTriangle className="w-7 h-7 text-[#C05621]" /> Waste &amp; Damage Tracking
        </h1>
        <p className="text-sm text-[#8B7355] mt-1">Log every unit of waste/damage/expiry. All entries require HO approval before stock is deducted and posted to the Waste &amp; Damage Expense ledger.</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-transparent p-0 gap-2 h-auto flex-wrap">
          <ColorTab value="new" tone="orange" icon={Plus} testid="wd-tab-new">New Entry</ColorTab>
          <ColorTab value="log" tone="slate" icon={Package} testid="wd-tab-log">Recent Log</ColorTab>
          {canApprove && <ColorTab value="approvals" tone="amber" icon={Clock} testid="wd-tab-approvals">Pending Approvals</ColorTab>}
          <ColorTab value="analytics" tone="blue" icon={TrendingDown} testid="wd-tab-analytics">Analytics</ColorTab>
        </TabsList>

        <TabsContent value="new" className="pt-4"><NewEntryPanel onCreated={() => setTab("log")} /></TabsContent>
        <TabsContent value="log" className="pt-4"><LogPanel /></TabsContent>
        {canApprove && <TabsContent value="approvals" className="pt-4"><ApprovalsPanel /></TabsContent>}
        <TabsContent value="analytics" className="pt-4"><AnalyticsPanel /></TabsContent>
      </Tabs>
    </div>
  );
}

function NewEntryPanel({ onCreated }) {
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState({ product_id: "", quantity: 1, loss_type: "waste", reason: "", batch_number: "", photo_data_url: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/products").then((r) => setProducts(r.data)).catch(() => {}); }, []);

  const lossTypeInfo = LOSS_TYPES.find((t) => t.value === form.loss_type);
  const photoRequired = !!lossTypeInfo?.photo;

  const handlePhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setForm((f) => ({ ...f, photo_data_url: String(ev.target.result || "") }));
    reader.readAsDataURL(file);
  };

  const save = async () => {
    if (!form.product_id) return toast.error("Choose a product");
    if (form.quantity <= 0) return toast.error("Quantity must be > 0");
    if ((form.reason || "").trim().length < 5) return toast.error("Reason must be at least 5 characters");
    if (photoRequired && !form.photo_data_url) return toast.error(`Photo is required for ${form.loss_type} entries`);
    setSaving(true);
    try {
      await api.post("/waste-damage", form);
      toast.success("Waste/Damage entry recorded — awaiting HO approval");
      setForm({ product_id: "", quantity: 1, loss_type: "waste", reason: "", batch_number: "", photo_data_url: "" });
      onCreated?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const selectedProduct = products.find((p) => p.id === form.product_id);

  return (
    <Card className="border-[#EAE5D9] max-w-3xl" data-testid="wd-new-panel">
      <CardHeader><CardTitle className="font-[Outfit]">Record Waste / Damage</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label>Product / SKU *</Label>
          <Select value={form.product_id} onValueChange={(v) => setForm({ ...form, product_id: v })}>
            <SelectTrigger className="bg-white" data-testid="wd-product"><SelectValue placeholder="Search & pick product" /></SelectTrigger>
            <SelectContent className="bg-white max-h-72">
              {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {p.sku || "no-sku"} (stock {p.current_stock ?? 0})</SelectItem>)}
            </SelectContent>
          </Select>
          {selectedProduct && (
            <div className="text-xs text-[#8B7355] mt-1">Cost: ₹{inr(selectedProduct.cost_price || selectedProduct.unit_cost || 0)}/{selectedProduct.unit || "unit"} · Current stock: {selectedProduct.current_stock ?? 0}</div>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Loss Type *</Label>
            <Select value={form.loss_type} onValueChange={(v) => setForm({ ...form, loss_type: v })}>
              <SelectTrigger className="bg-white" data-testid="wd-loss-type"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                {LOSS_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}{t.photo && " 📷"}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Quantity *</Label>
            <Input type="number" min="0.001" step="0.01" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: Number(e.target.value) })} data-testid="wd-quantity" />
          </div>
          <div className="col-span-2">
            <Label>Reason * (min 5 chars)</Label>
            <Textarea rows={2} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="e.g. Fell during transfer — bag torn / Expired on 15-Feb / QC rejected due to under-mix" data-testid="wd-reason" />
          </div>
          <div><Label>Batch # (optional)</Label><Input value={form.batch_number} onChange={(e) => setForm({ ...form, batch_number: e.target.value })} placeholder="BATCH-2026-042" /></div>
          <div>
            <Label>Photo {photoRequired && <span className="text-red-600">*</span>}<span className="text-[10px] text-[#8B7355] ml-1">(required for damage/expired)</span></Label>
            <Input type="file" accept="image/*" onChange={handlePhoto} className="bg-white" data-testid="wd-photo" />
            {form.photo_data_url && <img src={form.photo_data_url} alt="preview" className="mt-2 max-h-32 rounded border border-[#EAE5D9]" />}
          </div>
        </div>
        {photoRequired && !form.photo_data_url && <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 flex items-center gap-2"><Camera className="w-4 h-4" /> A photo is mandatory for {form.loss_type} entries.</div>}
        <div className="flex justify-end gap-2 pt-2 border-t border-[#EAE5D9]">
          <Button onClick={save} disabled={saving} className="bg-[#C05621] hover:bg-[#9C4221] text-white" data-testid="wd-save"><Plus className="w-4 h-4 mr-1" /> {saving ? "Recording…" : "Submit for Approval"}</Button>
        </div>
      </CardContent>
    </Card>
  );
}

function LogPanel() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      if (typeFilter !== "all") params.loss_type = typeFilter;
      setRows((await api.get("/waste-damage", { params })).data);
    } catch { toast.error("Failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter, typeFilter]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    if (!ql) return rows;
    return rows.filter((r) => (r.product_name || "").toLowerCase().includes(ql) || (r.reason || "").toLowerCase().includes(ql) || (r.batch_number || "").toLowerCase().includes(ql));
  }, [rows, q]);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[240px]"><Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-[#8B7355]" /><Input className="pl-7 bg-white" placeholder="Search product / reason / batch…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="wd-search" /></div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44 bg-white" data-testid="wd-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="pending_approval">Pending Approval</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-40 bg-white" data-testid="wd-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All types</SelectItem>
            {LOSS_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}><RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh</Button>
      </div>
      <Card className="border-[#EAE5D9]">
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-[#FAF6EE] text-xs uppercase tracking-wider text-[#8B7355]"><tr>
              <th className="text-left px-4 py-2.5">Date</th>
              <th className="text-left px-4 py-2.5">Product / SKU</th>
              <th className="text-right px-4 py-2.5">Qty</th>
              <th className="text-left px-4 py-2.5">Type</th>
              <th className="text-left px-4 py-2.5">Reason</th>
              <th className="text-right px-4 py-2.5">Loss ₹</th>
              <th className="text-left px-4 py-2.5">Reported By</th>
              <th className="text-left px-4 py-2.5">Status</th>
            </tr></thead>
            <tbody data-testid="wd-log-rows">
              {filtered.map((r) => {
                const typeInfo = LOSS_TYPES.find((t) => t.value === r.loss_type);
                return (
                  <tr key={r.id} className="border-t border-[#EAE5D9] hover:bg-[#FDFBF7]" data-testid={`wd-row-${r.id}`}>
                    <td className="px-4 py-2 text-xs">{fmtDate(r.reported_at)}<div className="text-[10px] text-[#8B7355]">{(r.reported_at || "").slice(11, 16)}</div></td>
                    <td className="px-4 py-2"><div className="font-medium">{r.product_name}</div>{r.sku && <div className="text-[10px] text-[#8B7355] font-mono">{r.sku}</div>}{r.batch_number && <div className="text-[10px] text-[#8B7355]">Batch: {r.batch_number}</div>}</td>
                    <td className="px-4 py-2 text-right font-mono">{r.quantity} {r.unit}</td>
                    <td className="px-4 py-2"><Badge variant="outline" className={`uppercase text-[10px] ${typeInfo?.tone || ""}`}>{r.loss_type}</Badge>{r.has_photo && <span className="ml-1 text-[10px] text-[#8B7355]">📷</span>}</td>
                    <td className="px-4 py-2 text-xs max-w-[240px] line-clamp-2">{r.reason}</td>
                    <td className="px-4 py-2 text-right font-mono text-red-700">{inr(r.total_loss)}</td>
                    <td className="px-4 py-2 text-xs">{r.reported_by_name}</td>
                    <td className="px-4 py-2"><Badge variant="outline" className={`uppercase text-[10px] ${STATUS_TONE[r.status] || ""}`}>{r.status.replace("_", " ")}</Badge></td>
                  </tr>
                );
              })}
              {filtered.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-[#8B7355]"><Package className="w-8 h-8 mx-auto mb-2 opacity-40" />No entries in this filter</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

function ApprovalsPanel() {
  const [rows, setRows] = useState([]);
  const [previewFor, setPreviewFor] = useState(null);
  const load = async () => {
    try { setRows((await api.get("/waste-damage", { params: { status: "pending_approval" } })).data); } catch { toast.error("Failed"); }
  };
  useEffect(() => { load(); }, []);

  const approve = async (r) => {
    const note = window.prompt(`Approve waste entry for ${r.product_name} × ${r.quantity} (₹${inr(r.total_loss)} loss)?\n\nOptional note:`);
    if (note === null) return;
    try { await api.post(`/waste-damage/${r.id}/approve`, { note }); toast.success("Approved · stock deducted · posted to ledger"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const reject = async (r) => {
    const reason = window.prompt("Rejection reason (required):");
    if (!reason?.trim()) return toast.error("Rejection reason is required");
    try { await api.post(`/waste-damage/${r.id}/reject`, { reason }); toast.success("Rejected"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <>
      <Card className="border-[#EAE5D9]">
        <CardHeader className="pb-2"><CardTitle className="font-[Outfit] text-base">Pending Approvals ({rows.length})</CardTitle></CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-[#FAF6EE] text-xs uppercase text-[#8B7355]"><tr>
              <th className="text-left px-4 py-2">Date</th>
              <th className="text-left px-4 py-2">Product</th>
              <th className="text-right px-4 py-2">Qty</th>
              <th className="text-left px-4 py-2">Type</th>
              <th className="text-left px-4 py-2">Reason</th>
              <th className="text-right px-4 py-2">Loss ₹</th>
              <th className="text-right px-4 py-2">Actions</th>
            </tr></thead>
            <tbody data-testid="wd-approvals-rows">
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-[#EAE5D9]" data-testid={`wd-approval-row-${r.id}`}>
                  <td className="px-4 py-2 text-xs">{fmtDate(r.reported_at)}</td>
                  <td className="px-4 py-2">{r.product_name}</td>
                  <td className="px-4 py-2 text-right font-mono">{r.quantity} {r.unit}</td>
                  <td className="px-4 py-2"><Badge variant="outline" className="uppercase text-[10px]">{r.loss_type}</Badge></td>
                  <td className="px-4 py-2 text-xs max-w-[300px]">{r.reason}<div className="text-[10px] text-[#8B7355]">by {r.reported_by_name}</div></td>
                  <td className="px-4 py-2 text-right font-mono text-red-700">{inr(r.total_loss)}</td>
                  <td className="px-4 py-2 text-right space-x-1">
                    {r.has_photo && <Button size="sm" variant="outline" onClick={() => setPreviewFor(r)} data-testid={`wd-photo-${r.id}`}><Camera className="w-3 h-3" /></Button>}
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => approve(r)} data-testid={`wd-approve-${r.id}`}><CheckCircle2 className="w-3 h-3 mr-1" /> Approve</Button>
                    <Button size="sm" variant="destructive" onClick={() => reject(r)} data-testid={`wd-reject-${r.id}`}><XCircle className="w-3 h-3 mr-1" /> Reject</Button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-[#8B7355]"><CheckCircle2 className="w-8 h-8 mx-auto mb-2 opacity-40" />No pending approvals — great job!</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>
      {previewFor && <PhotoPreview entry={previewFor} onClose={() => setPreviewFor(null)} />}
    </>
  );
}

function PhotoPreview({ entry, onClose }) {
  const [full, setFull] = useState(null);
  useEffect(() => { api.get(`/waste-damage/${entry.id}`).then((r) => setFull(r.data)).catch(() => {}); }, [entry]);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-[Outfit]">Damage Photo — {entry.product_name}</DialogTitle>
          <DialogDescription className="text-xs text-[#8B7355]">Reported by {entry.reported_by_name} on {fmtDate(entry.reported_at)}. Reason: {entry.reason}</DialogDescription>
        </DialogHeader>
        {full?.photo_data_url ? <img src={full.photo_data_url} alt="damage" className="w-full rounded border border-[#EAE5D9]" /> : <div className="text-center py-8 text-[#8B7355]">Loading photo…</div>}
      </DialogContent>
    </Dialog>
  );
}

function AnalyticsPanel() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/waste-damage/summary").then((r) => setData(r.data)).catch(() => toast.error("Failed")); }, []);
  if (!data) return <div className="p-6 text-[#8B7355]">Loading…</div>;

  return (
    <div className="space-y-4" data-testid="wd-analytics-panel">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Grand Total Loss" value={inr(data.grand_total_loss)} tone="red" />
        <Kpi label="Today's Loss" value={inr(data.today.total_loss)} sub={`${data.today.count} entries`} tone="amber" />
        <Kpi label="Pending Approvals" value={data.pending_approvals} tone="blue" />
        <Kpi label="Total Types" value={Object.keys(data.by_type).length} tone="slate" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="border-[#EAE5D9]">
          <CardHeader className="pb-2"><CardTitle className="font-[Outfit] text-base">By Loss Type</CardTitle></CardHeader>
          <CardContent>
            {LOSS_TYPES.map((t) => {
              const v = data.by_type[t.value];
              if (!v) return null;
              return (
                <div key={t.value} className="flex items-center justify-between py-2 border-b border-[#EAE5D9] last:border-0">
                  <div className="flex items-center gap-2"><Badge variant="outline" className={`${t.tone} uppercase text-[10px]`}>{t.value}</Badge><span className="text-sm">{t.label}</span></div>
                  <div className="text-right"><div className="font-mono font-semibold text-red-700">{inr(v.total)}</div><div className="text-[10px] text-[#8B7355]">{v.count} entries</div></div>
                </div>
              );
            })}
            {Object.keys(data.by_type).length === 0 && <div className="text-center py-6 text-[#8B7355] italic">No approved losses yet</div>}
          </CardContent>
        </Card>
        <Card className="border-[#EAE5D9]">
          <CardHeader className="pb-2"><CardTitle className="font-[Outfit] text-base">Top 5 Wasteful Products</CardTitle></CardHeader>
          <CardContent>
            {data.top_products.map((p, i) => (
              <div key={p.product_id} className="flex items-center justify-between py-2 border-b border-[#EAE5D9] last:border-0">
                <div className="flex items-center gap-2"><Badge className="bg-red-100 text-red-800 border-red-200" variant="outline">#{i + 1}</Badge><span className="text-sm font-medium">{p.product_name}</span></div>
                <div className="text-right"><div className="font-mono font-semibold text-red-700">{inr(p.total_loss)}</div><div className="text-[10px] text-[#8B7355]">{p.quantity} units · {p.entries} entries</div></div>
              </div>
            ))}
            {data.top_products.length === 0 && <div className="text-center py-6 text-[#8B7355] italic">No approved losses yet</div>}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

const Kpi = ({ label, value, sub, tone }) => {
  const map = { red: "bg-red-50 border-red-200 text-red-800", amber: "bg-amber-50 border-amber-200 text-amber-800", blue: "bg-blue-50 border-blue-200 text-blue-800", slate: "bg-slate-50 border-slate-200 text-slate-700" };
  return (
    <div className={`rounded-lg border p-3 ${map[tone] || ""}`}>
      <div className="text-[10px] uppercase tracking-wider opacity-80">{label}</div>
      <div className="text-2xl font-[Outfit] font-bold mt-1">{typeof value === "number" ? value : value}</div>
      {sub && <div className="text-[10px] opacity-70 mt-0.5">{sub}</div>}
    </div>
  );
};
